import React from 'react';

/**
 * `common/Spinner` (M-UX-2.3, architecture-ux-restructure.md §4.3).
 *
 * Central loading indicator — always Mint (`text-accent`, token-driven).
 * `role="status"` + `aria-live="polite"` make it announce to assistive tech
 * without stealing focus. `components/LoadingSpinner.tsx` and
 * `components/ui/LoadingSpinner.tsx` are now thin re-export wrappers around
 * this component (legacy prop signatures preserved for existing consumers).
 */
export interface SpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  /** Visible loading text under the spinner. Omit for a screen-reader-only label. */
  label?: string;
  className?: string;
}

const SIZE_CLASSES: Record<NonNullable<SpinnerProps['size']>, string> = {
  sm: 'h-4 w-4',
  md: 'h-8 w-8',
  lg: 'h-12 w-12',
};

/**
 * Mint (brand-accent) loading spinner with an accessible status announcement.
 */
export function Spinner({ size = 'md', label, className = '' }: SpinnerProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={['inline-flex flex-col items-center justify-center gap-2', className]
        .filter(Boolean)
        .join(' ')}
    >
      <svg
        className={`animate-spin text-accent ${SIZE_CLASSES[size]}`}
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      {label ? (
        <span className="text-sm text-secondary">{label}</span>
      ) : (
        <span className="sr-only">Wird geladen…</span>
      )}
    </div>
  );
}

export default Spinner;
